import type { components, ModalId, ModalViewAction } from '@heidi/schema/ml-scribe';

/**
 * The app inputs every Heidi II surface shares, bound ONCE from
 * `src/composition/hosts/agents/heidi-2-ports-wiring.ts` at `_app` module scope.
 *
 * Members cross BY REFERENCE and are called where they are called today. These
 * surfaces render conditionally and the awareness banner renders on every page,
 * so a value resolved at a mount would move a PostHog exposure or a query to a
 * render that does not happen today.
 *
 * Everything is typed in this package's own vocabulary or in `@heidi/schema`:
 * a foreign domain's door type would be an edge this seam exists to avoid.
 */

/** The `seen_modals` record a gate reads. */
export type Heidi2ModalView = components['schemas']['ModalView'];
/** The user fields the Heidi II gates read, narrowed off the wire shape. */
export type Heidi2UserRecord = Pick<
  components['schemas']['UserResponse'],
  'is_managed' | 'seen_modals'
>;
export type Heidi2BillingInfo = components['schemas']['BillingInfoResponseView'];
export type Heidi2CreditAllowance = components['schemas']['CreditAllowanceResponse'];

/** What `useQueuedModal` returns, narrowed to what the Heidi II surfaces read. */
export interface Heidi2QueuedModal {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  closeWithOptions: (options?: {
    action?: ModalViewAction;
    increment_count?: boolean;
  }) => void;
}

/** The three `appStateForModalsAtom` fields the welcome cohort reads. */
export interface Heidi2ModalLaunchState {
  isUserDataLoaded: boolean;
  isUserPending: boolean;
  isKindePropertiesPending: boolean;
}

/** The team-settings fields the opt-in gate reads, narrowed off the wire shape. */
export interface Heidi2TeamAccessEntitlements {
  team_entitlements?: { name?: string | null; enabled?: boolean | null }[];
}

export interface Heidi2MarkModalAsViewedInput {
  modal_id: ModalId;
  increment_count: boolean;
  action?: ModalViewAction;
}

/** Heidi II campaign strips that outrank the Scribe layout banner for an app load. */
export type Heidi2CampaignStrip = 'awareness' | 'request-access' | 'explore-later';

export interface Heidi2Ports {
  // ---- Audience and seen state ---------------------------------------------
  useIsHeidi2OnboardingEnabled: () => boolean;
  useUserBillingInfo: () => {
    data: Heidi2BillingInfo | undefined;
    isPending: boolean;
    isError: boolean;
    isFetching: boolean;
    /** When THIS load's response landed; a restored payload reads as settled. */
    dataUpdatedAt: number;
  };
  /**
   * The current-user query. It is a member rather than a direct read because
   * `/data` may not import `@heidi/workspace/data`; `/ui` reads that door itself.
   */
  useQueryUser: <T>(select: (user: Heidi2UserRecord | undefined) => T | undefined) => {
    data: T | undefined;
  };
  /** The queue's seen set, reactively — the Home dot re-renders on hydration. */
  useSeenModalIds: () => Set<ModalId> | null;
  isModalClosed: (modalId: ModalId, view: Heidi2ModalView | null | undefined) => boolean;

  // ---- The modal queue -----------------------------------------------------
  useQueuedModal: (modalId: ModalId) => Heidi2QueuedModal;
  /** The three `appStateForModalsAtom` fields the welcome cohort reads. */
  useModalLaunchState: () => Heidi2ModalLaunchState;
  /** The queue's HEAD — the modal that would show next, not the one showing. */
  useQueueHeadModalId: () => ModalId | undefined;
  /** The queue head, for surfaces that step aside rather than subscribe. */
  useCurrentQueuedModalId: () => ModalId | undefined;
  useMarkModalAsViewed: () => (input: Heidi2MarkModalAsViewedInput) => void;
  /**
   * The request-access write, from `@heidi/commerce/data` — a foreign domain's
   * state door, which neither zone here may import. By reference, so the
   * modal calls it at its own top level as it did before the move.
   */
  useMutateRequestHeidi2Access: (callbacks: {
    onRequested: (result: { adminNotificationsSent: number }) => void;
    onRequestError: () => void;
  }) => { mutate: (input: { message: string }) => void; isPending: boolean };

  // ---- Route and shell -----------------------------------------------------
  usePathname: () => string;
  /** Opens the Home panel, the way the Home nav item does. */
  useOpenHomePanel: () => () => void;
  /** `router.push`, so the skills item keeps its shallow navigation. */
  useNavigate: () => (href: string, options?: { shallow?: boolean }) => void;
  /** Starts a new chat, the way the sidebar's own control does. */
  useChatSidebarHandlers: () => { handleNewChat: () => void };
  /** `NANGO_SETTINGS_PATH` — the href, so agents takes no integrations edge. */
  connectAppsHref: string;
  /** `router.asPath`, which the tour reads to decide where to put someone back. */
  useRouterAsPath: () => string;
  /** The resolved Home href — Work home or the legacy chat home. */
  useBaseChatHref: () => string;
  /** True for a path the boot landed on Home at. */
  isHomeBootPath: (path: string) => boolean;
  /** Stages the Work-home view so the tour's finish lands on Home. */
  useSetPendingWorkHomeView: () => (view: 'home') => void;
  /** Reports the in-flow top strip so the layout reserves its height. */
  useReportTopStrip: (isShown: boolean) => void;
  /** Claims the shared top-strip slot; true only while this Heidi 2 strip wins. */
  useClaimTopStrip: (
    id: 'heidi-2-awareness' | 'heidi-2-request-access' | 'heidi-2-explore-later',
    isEligible: boolean
  ) => boolean;
  /** Current-load user hydration and flag resolution, not persisted-cache success. */
  useStripReadiness: () => { userId: string | undefined; isReady: boolean };
  useReportHeidi2Strip: (
    strip: Heidi2CampaignStrip,
    userId: string | undefined,
    readiness: 'pending' | 'ineligible' | 'showing'
  ) => void;

  // ---- Cohort, entitlements and identity -----------------------------------
  useIsHeidi2OptInEnabled: () => boolean;
  /** Flag-resolved welcome CTA copy; `undefined` off the experiment. */
  useHeidi2WelcomeCtaCopy: (locale: string) => string | undefined;
  /** Has the workspace id settled, the same gate the credit popover launch uses. */
  useWorkspaceIdSettledForCreditUsagePopoverLaunch: () => boolean;
  /**
   * True for a visit that started at the Dictate download entry. The welcome
   * takeover stands down for it: that visitor came to download Dictate, and
   * the download dialog is waiting for them. They get the tour next visit.
   */
  useIsHeidi2WelcomeSuppressed: () => boolean;
  useQueryRoles: (teamId: string | undefined) => {
    data?: { data?: { id: string }[] } | null;
    isFetching?: boolean;
    isError: boolean;
    isPending: boolean;
  };
  useQueryTeamAccessEntitlements: (teamId: string | undefined) => {
    data?: Heidi2TeamAccessEntitlements;
    isFetching?: boolean;
    isError: boolean;
    isPending: boolean;
  };
  /** Whether the signed-in user is already on a MAX billing seat. */
  isMaxBillingSeat: (
    billingInfo: Pick<Heidi2BillingInfo, 'seat_type'> | null | undefined
  ) => boolean;
  /** The clinician's canonical specialty, for the hero demo script. */
  useUserCanonicalSpecialtyName: () => string | undefined;
  /** The connected EHR's logo, or `undefined` for the generic tile. */
  useEhrLogoSrc: () => string | undefined;
  /** Switches the workspace to Heidi II. */
  useMutateActivateHeidi2: (callbacks: {
    onActivated: () => void;
    onActivationError: () => void;
  }) => { mutate: () => void; isPending: boolean };

  // ---- Recording, build profile and billing --------------------------------
  useIsClinicalRecordingActive: (includeChronicle: boolean) => boolean;
  useCreditAllowance: (enabled?: boolean) => {
    data: Heidi2CreditAllowance | null | undefined;
    isPending: boolean;
  };
  isComplimentaryCreditAllowance: (
    allowance: Heidi2CreditAllowance | null | undefined
  ) => boolean;
  isAppleCompliantBuild: () => boolean;
  /** A build constant, not a read: the profile is fixed when the bundle is built. */
  isOnPrem: boolean;
}

let ports: Heidi2Ports | null = null;

export const configureHeidi2Ports = (next: Heidi2Ports): void => {
  ports = next;
};

export const getHeidi2Ports = (): Heidi2Ports => {
  if (!ports) {
    throw new Error(
      '[agents/heidi-2-ports] ports not configured — the app must load ' +
        "'@/composition/hosts/agents/heidi-2-ports-wiring' at bootstrap; tests must call " +
        'configureHeidi2Ports() with a double first.'
    );
  }
  return ports;
};

export const resetHeidi2PortsForTests = (): void => {
  ports = null;
};
